import { createApi } from "@reduxjs/toolkit/query/react";
import { DynamicBaseQuery } from "./baseQuery";
import { MedicationIndexRequest } from "./types/medications/requests/MedicationsRequest";
import { MedicationIndexResponse } from "./types/medications/responses/MedicationIndexResponse";
import { ShowMedicationResponse } from "./types/medications/responses/ShowMedicationResponse";
import { CreateMedicationRequest } from "./types/medications/requests/CreateMedicationRequest";
import { SuccessResponse } from "./types/common/responses/SuccessResponse";
import { UpdateMedicationRequest } from "./types/medications/requests/UpdateMedicationRequest";
import { MedicationStatusEnum } from "./types/medications/enums/MedicationStatusEnum";
import { ActionSignatureRequest } from "./types/common/requests/ActionSignatureRequest";
import { AdministerMedicationRequest } from "./types/medications/requests/AdministerMedicationRequest";
import { MedicationReportResponse } from "./types/medications/responses/MedicationReportResponse";
import { MedicationReportRequest } from "./types/medications/requests/MedicationReportRequest";
import { SignStatusEnums } from "./types/medications/enums/SignStatusEnums";
import { AdministerBulkMedicationRequest } from "./types/medications/requests/AdministerBulkMedicationRequest";
import { MedicationPostSubmissionNoteModel } from "./types/medications/models/MedicationPostSubmissionNoteModel";
import { PendingSignOffsResponse } from "./types/medications/responses/PendingSignOffsResponse";
import { PendingSignOffsRequest } from "./types/medications/requests/PendingSignOffsRequest";

export const medicationsApi = createApi({
  reducerPath: "medicationsApi",
  baseQuery: DynamicBaseQuery,
  tagTypes: [
    "CurrentMedications",
    "DiscontinuedMedications",
    "MedicationReport",
    "Medication",
    "PendingSignatureMedications",
  ],
  endpoints: (builder) => ({
    getCurrentMedications: builder.query<
      MedicationIndexResponse,
      MedicationIndexRequest
    >({
      query: (params) => ({
        url: "/medications",
        method: "GET",
        params: {
          ...params,
          filter: {
            ...params.filter,
            status: MedicationStatusEnum.ACTIVE,
          },
        },
      }),
      providesTags: (_result, _error, { filter }) => [
        { type: "CurrentMedications", id: filter?.resident_id ?? "All" },
      ],
    }),
    getDiscontinuedMedications: builder.query<
      MedicationIndexResponse,
      MedicationIndexRequest
    >({
      query: (params) => ({
        url: "/medications",
        method: "GET",
        params: {
          ...params,
          filter: {
            ...params.filter,
            status: MedicationStatusEnum.DISCONTINUED,
          },
        },
      }),
      providesTags: (_result, _error, { filter }) => [
        { type: "DiscontinuedMedications", id: filter?.resident_id ?? "All" },
      ],
    }),
    getPendingSignatureMedications: builder.query<
      MedicationIndexResponse,
      MedicationIndexRequest
    >({
      query: (params) => ({
        url: "/medications",
        method: "GET",
        params: {
          ...params,
          filter: {
            ...params.filter,
            sign_status: SignStatusEnums.PENDING_SIGNATURE,
          },
        },
      }),
      providesTags: (_result, _error, { filter }) => [
        {
          type: "PendingSignatureMedications",
          id: filter?.resident_id ?? "All",
        },
      ],
    }),
    createMedication: builder.mutation<
      SuccessResponse,
      CreateMedicationRequest
    >({
      query: (body) => ({
        url: "/medications",
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { resident_id: residentId }) => [
        { type: "CurrentMedications", id: residentId },
      ],
    }),
    updateMedication: builder.mutation<
      ShowMedicationResponse,
      UpdateMedicationRequest
    >({
      query: (data) => ({
        url: `/medications/${data.id}`,
        method: "PUT",
        body: data.body,
      }),
      invalidatesTags: (
        _result,
        _error,
        { body: { resident_id: residentId } },
      ) => [
        { type: "CurrentMedications", id: residentId },
        { type: "DiscontinuedMedications", id: residentId },
      ],
    }),
    showMedication: builder.query<
      ShowMedicationResponse,
      { medication: number }
    >({
      query: ({ medication }) => ({
        url: `/medications/${medication}`,
        method: "GET",
      }),
      providesTags: (_result, _error, { medication }) => [
        { type: "Medication", id: medication },
      ],
    }),
    discontinueMedication: builder.mutation<
      SuccessResponse,
      { medication: number; residentId: number; body: ActionSignatureRequest }
    >({
      query: ({ medication, body }) => ({
        url: `/medications/${medication}/status`,
        method: "PATCH",
        body: {
          ...body,
          status: MedicationStatusEnum.DISCONTINUED,
        },
      }),
      invalidatesTags: (_result, _error, { residentId }) => [
        { type: "CurrentMedications", id: residentId },
        { type: "DiscontinuedMedications", id: residentId },
      ],
    }),
    createMedicationReport: builder.mutation<
      SuccessResponse,
      { body: AdministerMedicationRequest }
    >({
      query: ({ body }) => ({
        url: `/medications/reports`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { body }) => [
        { type: "CurrentMedications", id: body.resident_id },
        { type: "MedicationReport", id: body.resident_id },
      ],
    }),
    createBulkMedicationReport: builder.mutation<
      SuccessResponse,
      { body: AdministerBulkMedicationRequest }
    >({
      query: ({ body }) => ({
        url: `/medications/reports/bulk`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { body }) => {
        const residentId = body?.[0]?.resident_id;
        return residentId ? [{ type: "MedicationReport", id: residentId }] : [];
      },
    }),
    getMedicationReport: builder.query<
      MedicationReportResponse,
      MedicationReportRequest
    >({
      query: (params) => ({
        url: "/medications/reports",
        method: "GET",
        params: {
          ...params,
          filter: {
            ...params.filter,
          },
        },
      }),
      providesTags: (_result, _error, { filter }) => [
        { type: "MedicationReport", id: filter?.resident_id ?? "All" },
      ],
    }),
    signOffMedication: builder.mutation<
      ShowMedicationResponse,
      { medication: number; residentId: number; body: ActionSignatureRequest }
    >({
      query: ({ medication, body }) => ({
        url: `/medications/${medication}/sign-off`,
        method: "PATCH",
        body: {
          ...body,
          status: SignStatusEnums.SIGNED,
        },
      }),
      invalidatesTags: (_result, _error, { residentId, medication }) => [
        { type: "CurrentMedications", id: residentId },
        { type: "PendingSignatureMedications", id: residentId },
        { type: "Medication", id: medication },
      ],
    }),
    getMedicationsPendingSignOffs: builder.query<
      PendingSignOffsResponse,
      PendingSignOffsRequest
    >({
      query: (params) => ({
        url: `/residents/pending-sign-offs/medications`,
        method: "GET",
        params,
      }),
    }),
    addPostSubmissionNote: builder.mutation<
      SuccessResponse,
      MedicationPostSubmissionNoteModel
    >({
      query: (body) => ({
        url: `/medications/reports/post-submission-note`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["MedicationReport"],
    }),
    voidMedicationLog: builder.mutation<
      SuccessResponse,
      { medicationLogId: number }
    >({
      query: ({ medicationLogId }) => ({
        url: `medications/reports/${medicationLogId}/void`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { medicationLogId }) => [
        { type: "MedicationReport", id: medicationLogId },
        "MedicationReport",
      ],
    }),
  }),
});

export const {
  useGetCurrentMedicationsQuery,
  useLazyGetCurrentMedicationsQuery,
  useGetDiscontinuedMedicationsQuery,
  useLazyGetDiscontinuedMedicationsQuery,
  useGetPendingSignatureMedicationsQuery,
  useLazyGetPendingSignatureMedicationsQuery,
  useCreateMedicationMutation,
  useCreateBulkMedicationReportMutation,
  useUpdateMedicationMutation,
  useShowMedicationQuery,
  useDiscontinueMedicationMutation,
  useCreateMedicationReportMutation,
  useGetMedicationReportQuery,
  useLazyGetMedicationReportQuery,
  useSignOffMedicationMutation,
  useGetMedicationsPendingSignOffsQuery,
  useAddPostSubmissionNoteMutation,
  useVoidMedicationLogMutation,
} = medicationsApi;
